import React, { useCallback, useEffect, useState } from 'react'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Alert from '@mui/material/Alert'
import Paper from '@mui/material/Paper'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import IconButton from '@mui/material/IconButton'
import Tooltip from '@mui/material/Tooltip'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogActions from '@mui/material/DialogActions'
import DeleteIcon from '@mui/icons-material/Delete'
import RefreshIcon from '@mui/icons-material/Refresh'
import { toast } from 'react-toastify'
import StreamingManagementService from '../../services/StreamingManagementService'
import { GcpRawChannel } from '../types'
import { GcpChannelStateBadge } from './StatusBadge'

function shortName(fullName: string) {
  const parts = fullName.split('/')
  return parts[parts.length - 1] || fullName
}

function formatDate(iso: string) {
  if (!iso) return '—'
  return new Date(iso).toLocaleString('pt-BR')
}

export default function GcpChannelsList() {
  const [channels, setChannels] = useState<GcpRawChannel[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [deleteTarget, setDeleteTarget] = useState<GcpRawChannel | null>(null)
  const [deleting, setDeleting] = useState(false)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await StreamingManagementService.getGcpChannels()
      setChannels(res?.data?.channels ?? [])
    } catch {
      setError('Não foi possível carregar os channels GCP.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return
    setDeleting(true)
    try {
      await StreamingManagementService.deleteGcpChannel(shortName(deleteTarget.name))
      toast.success(`Channel "${shortName(deleteTarget.name)}" removido.`)
      setDeleteTarget(null)
      fetchData()
    } catch (err: any) {
      const msg =
        err?.response?.data?.message ?? 'Erro ao remover o channel GCP.'
      toast.error(msg)
    } finally {
      setDeleting(false)
    }
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h6">Channels GCP</Typography>
        <Tooltip title="Atualizar">
          <span>
            <IconButton onClick={fetchData} disabled={loading}>
              <RefreshIcon />
            </IconButton>
          </span>
        </Tooltip>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" py={6}>
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Nome</TableCell>
                <TableCell>Estado</TableCell>
                <TableCell>Input Ativo</TableCell>
                <TableCell>Output URI</TableCell>
                <TableCell>Criado em</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {channels.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ py: 4, color: 'text.secondary' }}>
                    Nenhum channel encontrado.
                  </TableCell>
                </TableRow>
              ) : (
                channels.map((ch) => (
                  <TableRow key={ch.name} hover>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {shortName(ch.name)}
                    </TableCell>
                    <TableCell>
                      <GcpChannelStateBadge
                        state={
                          (ch.streamingState as any) ?? null
                        }
                      />
                    </TableCell>
                    <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>
                      {ch.activeInput ? shortName(ch.activeInput) : '—'}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <Tooltip title={ch.output?.uri || ''}>
                        <span>{ch.output?.uri || '—'}</span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>{formatDate(ch.createTime)}</TableCell>
                    <TableCell align="center">
                      <Tooltip title="Remover channel">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteTarget(ch)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {/* Confirm Delete Dialog */}
      <Dialog open={!!deleteTarget} onClose={() => !deleting && setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Remover Channel</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja remover o channel{' '}
            <strong>{deleteTarget ? shortName(deleteTarget.name) : ''}</strong>?
          </DialogContentText>
          <Alert severity="warning" sx={{ mt: 2 }}>
            Se o canal estiver em execução, a remoção pode levar alguns minutos para concluir.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)} disabled={deleting}>
            Cancelar
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={16} color="inherit" /> : <DeleteIcon />}
          >
            Remover
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
